import { useEffect, useMemo, useState, useCallback } from 'react';
import type { Layout, ResponsiveLayouts } from 'react-grid-layout';
import {
  ROLE_LAYOUTS,
  resolveRoleKey,
  type DashboardRoleKey,
  type RoleLayout,
  type RoleWidget,
} from '@/pages/dashboard/roleLayouts';
import { generateDefaultLayouts } from '@/pages/dashboard/widgets/widgetRegistry';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/utils/api';

/**
 * useRoleDashboard — the Luke Wroblewski "login moment" hook.
 *
 * Resolves the logged-in user to a DashboardRoleKey via Alpha's `resolveRoleKey`,
 * derives the default widget layout from ROLE_LAYOUTS[key], then attempts to
 * GET /analytics/user-dashboard-layout. If a persisted layout exists, it wins.
 * Otherwise we render the role default.
 *
 * The grid library (react-grid-layout) is unchanged — we just feed it different
 * `enabledWidgetIds` + `layouts` based on role.
 */

interface PersistedLayout {
  enabledWidgetIds: string[];
  layouts: ResponsiveLayouts;
}

interface UseRoleDashboardResult {
  roleKey: DashboardRoleKey;
  roleLayout: RoleLayout;
  /** Widget IDs currently rendered — either from saved custom layout or role default. */
  enabledWidgetIds: string[];
  /** react-grid-layout ResponsiveLayouts object fed to <WidgetGrid />. */
  layouts: ResponsiveLayouts;
  /** True while we're loading the user's persisted layout. */
  loading: boolean;
  /** True iff the user has saved a custom layout (i.e. overrides the role default). */
  isCustom: boolean;
  /** Persist the current layout to the server. */
  saveLayout: (enabledWidgetIds: string[], layouts: ResponsiveLayouts) => Promise<void>;
  /** Clear the user's custom layout — falls back to role default. */
  resetToDefault: () => Promise<void>;
  /** Handler compatible with <WidgetGrid onLayoutChange>. */
  handleLayoutChange: (current: Layout, all: ResponsiveLayouts) => void;
  /** Toggle a widget on/off and persist. */
  toggleWidget: (id: string) => Promise<void>;
}

function buildRoleDefault(widgets: RoleWidget[]): PersistedLayout {
  const enabledWidgetIds = widgets.map((w) => w.widgetKey);
  // Hint the grid with per-widget sizing by mapping the RoleWidget w/h into
  // the generator's defaults. We pass the ids and rely on widgetRegistry
  // `defaultLayout` — Bravo keeps those in sync with the role spec.
  const layouts = generateDefaultLayouts(enabledWidgetIds) as unknown as ResponsiveLayouts;
  return { enabledWidgetIds, layouts };
}

export function useRoleDashboard(): UseRoleDashboardResult {
  const { user } = useAuthStore();

  const roleKey = useMemo<DashboardRoleKey>(() => resolveRoleKey(user as any), [user]);
  const roleLayout = ROLE_LAYOUTS[roleKey];

  const roleDefault = useMemo<PersistedLayout>(
    () => buildRoleDefault(roleLayout.widgets),
    [roleLayout],
  );

  const [enabledWidgetIds, setEnabledWidgetIds] = useState<string[]>(roleDefault.enabledWidgetIds);
  const [layouts, setLayouts] = useState<ResponsiveLayouts>(roleDefault.layouts);
  const [loading, setLoading] = useState(true);
  const [isCustom, setIsCustom] = useState(false);

  // Load persisted layout on mount / role change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get('/analytics/user-dashboard-layout');
        if (cancelled) return;
        const custom: PersistedLayout | null = res.data?.data?.customLayout ?? null;
        if (custom && Array.isArray(custom.enabledWidgetIds) && custom.layouts) {
          setEnabledWidgetIds(custom.enabledWidgetIds);
          setLayouts(custom.layouts);
          setIsCustom(true);
        } else {
          setEnabledWidgetIds(roleDefault.enabledWidgetIds);
          setLayouts(roleDefault.layouts);
          setIsCustom(false);
        }
      } catch {
        if (!cancelled) {
          setEnabledWidgetIds(roleDefault.enabledWidgetIds);
          setLayouts(roleDefault.layouts);
          setIsCustom(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roleDefault]);

  const saveLayout = useCallback(
    async (nextIds: string[], nextLayouts: ResponsiveLayouts) => {
      try {
        await api.put('/analytics/user-dashboard-layout', {
          enabledWidgetIds: nextIds,
          layouts: nextLayouts,
        });
        setIsCustom(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to persist dashboard layout', err);
      }
    },
    [],
  );

  const resetToDefault = useCallback(async () => {
    try {
      await api.delete('/analytics/user-dashboard-layout');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to reset dashboard layout', err);
    }
    setEnabledWidgetIds(roleDefault.enabledWidgetIds);
    setLayouts(roleDefault.layouts);
    setIsCustom(false);
  }, [roleDefault]);

  const handleLayoutChange = useCallback(
    (_current: Layout, all: ResponsiveLayouts) => {
      setLayouts(all);
      // Persist opportunistically — fire and forget, no await
      void saveLayout(enabledWidgetIds, all);
    },
    [enabledWidgetIds, saveLayout],
  );

  const toggleWidget = useCallback(
    async (id: string) => {
      const nextIds = enabledWidgetIds.includes(id)
        ? enabledWidgetIds.filter((x) => x !== id)
        : [...enabledWidgetIds, id];
      const nextLayouts = generateDefaultLayouts(nextIds) as unknown as ResponsiveLayouts;
      setEnabledWidgetIds(nextIds);
      setLayouts(nextLayouts);
      await saveLayout(nextIds, nextLayouts);
    },
    [enabledWidgetIds, saveLayout],
  );

  return {
    roleKey,
    roleLayout,
    enabledWidgetIds,
    layouts,
    loading,
    isCustom,
    saveLayout,
    resetToDefault,
    handleLayoutChange,
    toggleWidget,
  };
}
